import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Box,
} from '@mui/material';
import { CheckCircle, Schedule } from '@mui/icons-material';
import {
  PROVIDERS,
  PROVIDER_CHOICE,
  DEFERRED_OPTION,
  ProviderOption,
  ProviderSelection,
} from 'constants/providers';
import { fetchEnabledProviders } from 'Helpers/providersApi';

/**
 * Eleccion del proveedor de pago durante el onboarding.
 *
 * El objetivo de este paso es que el abogado pueda decidir entre:
 *  - Usio, Dwolla o ambos: sigue el formulario de KYC de ese proveedor.
 *  - "Configure later": se crea su solicitud SIN informacion de proveedor y pasa
 *    directo a elegir su subdominio, para que pueda entrar a su portal de
 *    inmediato y completar el KYC mas tarde desde `/payment-setup`.
 *
 * La seleccion NO se envia al backend aqui: se guarda en el contexto de
 * enrolamiento y la consume el submit del wizard, que es quien decide si crea la
 * solicitud con o sin informacion.
 */

interface Props {
  /** Eleccion previa (si el usuario vuelve atras). */
  value: ProviderSelection | null;
  /** Se llama al confirmar, con el codigo elegido. */
  onContinue: (_choice: ProviderSelection) => void;
  /** Muestra un error si el paso previo fallo (p. ej. crear la solicitud diferida). */
  error?: string | null;
  /** Deshabilita la interaccion mientras el paso previo esta trabajando. */
  saving?: boolean;
  /**
   * En `/payment-setup` el portal ya existe: no se ofrece "Configure later".
   * Por defecto true para no cambiar el onboarding.
   */
  showDeferred?: boolean;
}

const optionKey = (option: ProviderOption | typeof DEFERRED_OPTION) => (
  option === DEFERRED_OPTION ? DEFERRED_OPTION : option.code
);

const ProviderSelectionStep: React.FC<Props> = ({
  value,
  onContinue,
  error,
  saving,
  showDeferred = true,
}) => {
  const [selected, setSelected] = useState<ProviderSelection | null>(value ?? null);

  /**
   * Arranca con el catalogo completo (no vacio) para no mostrar un paso en
   * blanco mientras carga. Si la consulta falla, `fetchEnabledProviders` deja
   * este mismo valor, que es el comportamiento *fail-open*.
   */
  const [enabledProviders, setEnabledProviders] = useState<ProviderOption[]>(PROVIDERS);
  const [loadingProviders, setLoadingProviders] = useState(true);

  useEffect(() => {
    let active = true;

    fetchEnabledProviders()
      .then((providers) => {
        if (!active) return;
        setEnabledProviders(providers);

        /**
         * Si la seleccion guardada en localStorage apunta a un proveedor que el
         * admin deshabilito, se descarta: es preferible pedir que elija de nuevo
         * a confirmar un proveedor que ya no se ofrece.
         */
        setSelected((current) => {
          if (!current || current === DEFERRED_OPTION) return current;
          const stillAvailable = providers.some((provider) => provider.code === current);
          return stillAvailable ? current : null;
        });
      })
      .finally(() => {
        if (active) setLoadingProviders(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const options: Array<ProviderOption | typeof DEFERRED_OPTION> = showDeferred
    ? [...enabledProviders, DEFERRED_OPTION]
    : enabledProviders;

  const handleContinue = () => {
    if (!selected) return;
    onContinue(selected);
  };

  /** Enter/Espacio seleccionan, como un radio nativo. */
  const handleKeyDown = (event: React.KeyboardEvent, key: ProviderSelection) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setSelected(key);
    }
  };

  return (
    <Grid
      container
      spacing={3}
      style={{ justifyContent: 'center', paddingTop: '25px' }}
    >
      <Grid
        item
        xs={12}
      >
        <Typography
          variant='h5'
          align='center'
          gutterBottom
          style={{ fontWeight: 'bold' }}
        >
          How would you like to get paid?
        </Typography>
        <Typography
          variant='body2'
          align='center'
          color='text.secondary'
          style={{ marginBottom: '10px' }}
        >
          You can start using your portal right away and set this up later if you prefer.
        </Typography>
      </Grid>

      {error && (
        <Grid
          item
          xs={12}
          sm={10}
        >
          <Alert severity='error'>{error}</Alert>
        </Grid>
      )}

      {options.map((option) => {
        const key = optionKey(option);
        const isDeferred = key === DEFERRED_OPTION;
        const isSelected = selected === key;

        // Los "Chip" y el copy cambian segun sea un proveedor real o el diferido.
        const title = isDeferred ? 'Configure later' : (option as ProviderOption).name;
        const description = isDeferred
          ? 'Create your subdomain now and add your payment information later from your portal.'
          : (option as ProviderOption).description;
        const methods = isDeferred ? [] : (option as ProviderOption).methods;

        return (
          <Grid
            item
            xs={12}
            sm={10}
            md={6}
            lg={5}
            key={key}
          >
            <Card
              className='card-custom'
              role='radio'
              aria-checked={isSelected}
              aria-label={title}
              tabIndex={0}
              onClick={() => setSelected(key)}
              onKeyDown={(event) => handleKeyDown(event, key)}
              style={{
                cursor: saving ? 'default' : 'pointer',
                height: '100%',
                // El borde es la unica senal visual de seleccion ademas del icono.
                // El rosa literal que habia aqui no existia en ninguna paleta;
                // pasa al indigo de marca, que si responde al tema.
                border: isSelected ? '2px solid var(--brand)' : '2px solid transparent',
                opacity: saving ? 0.6 : 1,
              }}
            >
              <CardContent style={{ textAlign: 'center', position: 'relative' }}>
                {isSelected && (
                  <CheckCircle
                    style={{ position: 'absolute', top: 12, right: 12, color: 'var(--brand)' }}
                    aria-hidden='true'
                  />
                )}

                {isDeferred ? (
                  <Schedule fontSize='large' color='action' aria-hidden='true' />
                ) : null}

                <Typography
                  variant='h6'
                  style={{ fontWeight: 'bold', marginTop: '8px' }}
                >
                  {title}
                </Typography>

                {methods.length > 0 && (
                  <Box style={{ margin: '8px 0', display: 'flex', gap: 6, justifyContent: 'center' }}>
                    {methods.map((method) => (
                      <Chip
                        key={method}
                        label={method}
                        size='small'
                        variant='outlined'
                      />
                    ))}
                  </Box>
                )}

                {!isDeferred && (option as ProviderOption).requiresDocuments && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    display='block'
                  >
                    Requires identity documents
                  </Typography>
                )}

                <Typography
                  variant='body2'
                  color='text.secondary'
                  style={{ marginTop: '8px' }}
                >
                  {description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        );
      })}

      <Grid
        item
        xs={12}
        sm={10}
        style={{ textAlign: 'center' }}
      >
        <Button
          variant='contained'
          color='primary'
          disabled={!selected || saving || loadingProviders}
          onClick={handleContinue}
          style={{ padding: '10px 40px', fontSize: '16px' }}
        >
          {saving ? <CircularProgress size={24} color='inherit' /> : 'Continue'}
        </Button>
      </Grid>
    </Grid>
  );
};

export { PROVIDER_CHOICE, DEFERRED_OPTION };
export default ProviderSelectionStep;
